import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Check,
  EyeOff,
  Image,
  ImageOff,
  ImagePlus,
  Loader2,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isToday, isYesterday } from "date-fns";

import {
  approve_review_recording,
  delete_review_recording,
  formatApiRequestError,
  get_review_recordings,
  get_review_screenshots,
  review_screenshot_url,
} from "../../api";
import type { ReviewApp, ReviewRecording, StatusPayload } from "../../api/types";
import { frontendCapabilities } from "../../globals";
import { formatDuration } from "../../utils";
import Modal from "../Modal";
import RecordingDeleteDialog from "../RecordingDeleteDialog";
import {
  ScreenshotCarouselDialog,
  type ScreenshotRef,
  type ScreenshotUrlResolver,
} from "../screenshots";
import { appIconUri } from "../processes/appIcon";
import { splitHash } from "../../utils/urlHash";
import { PENDING_REVIEW_SECTION } from "./pendingReviewHref";

/** Recordings awaiting review are still on this computer: their frames come from the core. */
const reviewScreenshotUrl: ScreenshotUrlResolver = async ({ recordingId, filename }) =>
  review_screenshot_url(recordingId, filename);

/** Apps shown per recording; the rest of a long session's apps would only be noise. */
const MAX_APPS = 5;
/** Window titles shown per app before "+n more". */
const MAX_WINDOW_TITLES = 3;

const ReviewTimestamp = ({ startedAt }: { startedAt: string }) => {
  const { t, i18n } = useTranslation();
  const date = new Date(startedAt);
  const day = isToday(date)
    ? t("review_today")
    : isYesterday(date)
      ? t("review_yesterday")
      : date.toLocaleDateString(i18n.resolvedLanguage, { dateStyle: "medium" });
  const time = date.toLocaleTimeString(i18n.resolvedLanguage, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return (
    <time
      dateTime={startedAt}
      title={date.toLocaleString(i18n.resolvedLanguage, { hourCycle: "h23" })}
    >
      {day}, {time}
    </time>
  );
};

const ReviewAppName = ({ name }: { name: string }) => {
  const icon = appIconUri(name);
  return (
    <span className="inline-flex items-center gap-1.5">
      {icon && <img src={icon} alt="" className="h-4 w-4 shrink-0 object-contain" />}
      {name}
    </span>
  );
};

/** Every excluded app is recorded as the same placeholder, so they are named together. */
const ExcludedAppsName = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5">
      <EyeOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      {t("review_excluded_apps")}
    </span>
  );
};

const ReviewActions = ({
  busy,
  approving,
  onApprove,
  onDelete,
}: {
  busy: boolean;
  approving: boolean;
  onApprove: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        disabled={busy}
        aria-busy={approving}
        onClick={onApprove}
        className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-info px-3 py-2 text-sm font-medium text-white transition hover:bg-info-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info disabled:cursor-not-allowed disabled:opacity-60"
      >
        {approving ? (
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        ) : (
          <Upload size={16} aria-hidden="true" />
        )}
        {approving ? t("loading_ellipsis") : t("review_approve_recording")}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        className="inline-flex min-h-9 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-danger transition hover:bg-danger/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Trash2 size={16} aria-hidden="true" />
        {t("delete_recording")}
      </button>
    </>
  );
};

type ReviewAppListProps = {
  apps: ReviewApp[];
  /** Total time in excluded apps, of which nothing else is known. */
  excludedSeconds: number;
};

const ReviewAppList = ({ apps, excludedSeconds }: ReviewAppListProps) => {
  const { t } = useTranslation();

  if (apps.length === 0 && excludedSeconds === 0) {
    return <p className="mt-3 text-xs text-text-dim">{t("review_no_apps")}</p>;
  }

  return (
    <div className="mt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-text-dim">
        {t("review_apps_heading")}
      </p>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {apps.slice(0, MAX_APPS).map(app => {
          const hiddenTitles = app.window_titles.length - MAX_WINDOW_TITLES;
          return (
            <li key={app.name} className="text-sm">
              <span className="font-medium text-text">
                <ReviewAppName name={app.name} />
              </span>
              <span className="ml-2 text-xs text-text-dim tabular-nums">
                {formatDuration(app.seconds)}
              </span>
              <ul className="mt-0.5 flex flex-col text-xs text-text-mid">
                {app.window_titles.slice(0, MAX_WINDOW_TITLES).map(window => (
                  <li key={window.title} className="truncate" title={window.title}>
                    {window.title || t("review_untitled_window")}
                  </li>
                ))}
                {hiddenTitles > 0 && (
                  <li className="text-text-dim">
                    {t("review_more_window_titles", { count: hiddenTitles })}
                  </li>
                )}
              </ul>
            </li>
          );
        })}
        {/* Last and outside the app limit, so a long session's apps never hide it. */}
        {excludedSeconds > 0 && (
          <li className="text-sm">
            <span className="font-medium text-text">
              <ExcludedAppsName />
            </span>
            <span className="ml-2 text-xs text-text-dim tabular-nums">
              {t("review_excluded_apps_total", { duration: formatDuration(excludedSeconds) })}
            </span>
            <p className="mt-0.5 text-xs text-text-mid">{t("review_excluded_apps_hint")}</p>
          </li>
        )}
      </ul>
    </div>
  );
};

type ReviewRecordingCardProps = {
  recording: ReviewRecording;
  /** Screenshots the user left out of the upload so far. */
  excludedCount: number;
  busy: boolean;
  approving: boolean;
  loadingScreenshots: boolean;
  onApprove: () => void;
  onDelete: () => void;
  onViewScreenshots: () => void;
};

const ReviewRecordingCard = ({
  recording,
  excludedCount,
  busy,
  approving,
  loadingScreenshots,
  onApprove,
  onDelete,
  onViewScreenshots,
}: ReviewRecordingCardProps) => {
  const { t } = useTranslation();
  const stats = [
    t("review_n_events", { count: recording.event_count }),
    t("review_n_clicks", { count: recording.total_clicks }),
    t("review_n_shortcuts", { count: recording.shortcut_count }),
    t("review_n_typed_chars", { count: recording.typed_chars }),
  ];

  return (
    <article className="min-w-0 rounded-xl border border-outline bg-surface p-4">
      <div className="flex flex-col justify-between gap-4 @[1000px]:flex-row @[1000px]:items-start">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text">
            <ReviewTimestamp startedAt={recording.started_at} />
          </h3>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-mid">
            <span>{formatDuration(recording.duration_seconds)}</span>
            <span>{t("review_n_screenshots", { count: recording.screenshot_count })}</span>
            {excludedCount > 0 && <span>{t("review_n_excluded", { count: excludedCount })}</span>}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-dim">
            {recording.apps.length || recording.excluded_app_seconds ? (
              <>
                {recording.apps.slice(0, MAX_APPS).map(app => (
                  <ReviewAppName key={app.name} name={app.name} />
                ))}
                {recording.excluded_app_seconds > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <ExcludedAppsName />
                    <span className="tabular-nums">
                      {formatDuration(recording.excluded_app_seconds)}
                    </span>
                  </span>
                )}
              </>
            ) : (
              t("review_no_apps")
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy || recording.screenshot_count === 0}
            aria-busy={loadingScreenshots}
            onClick={onViewScreenshots}
            className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingScreenshots ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
              <Image size={16} aria-hidden="true" />
            )}
            {t("review_screenshots")}
          </button>
          <ReviewActions
            busy={busy}
            approving={approving}
            onApprove={onApprove}
            onDelete={onDelete}
          />
        </div>
      </div>
      <details className="mt-3 border-t border-outline pt-3">
        <summary className="w-fit cursor-pointer rounded text-xs font-medium text-text-mid hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info">
          {t("review_details")}
        </summary>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-dim">
          {stats.map(stat => (
            <li key={stat}>{stat}</li>
          ))}
        </ul>
        <ReviewAppList apps={recording.apps} excludedSeconds={recording.excluded_app_seconds} />
      </details>
    </article>
  );
};

type ApproveReviewDialogProps = {
  /** The recording whose upload is being confirmed; the dialog is open while there is one. */
  recording?: ReviewRecording;
  /** Screenshots left out of its upload, deleted on approval. */
  excludedCount: number;
  busy: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

/** Confirms a recording's upload, saying how many of its screenshots are deleted first. */
const ApproveReviewDialog = ({
  recording,
  excludedCount,
  busy,
  error,
  onCancel,
  onConfirm,
}: ApproveReviewDialogProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={recording !== undefined}
      onOpenChange={nextOpen => {
        if (!nextOpen) onCancel();
      }}
      busy={busy}
    >
      {recording && (
        <>
          <div className="pr-10">
            <Dialog.Title className="text-lg font-semibold text-text">
              {excludedCount > 0
                ? t("review_exclude_confirm_title", {
                    excluded: excludedCount,
                    count: recording.screenshot_count,
                  })
                : t("review_upload_all_confirm_title")}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm leading-5 text-text-mid">
              {t(
                excludedCount > 0
                  ? "review_exclude_confirm_description"
                  : "review_upload_all_confirm_description",
              )}
            </Dialog.Description>
          </div>
          {error && (
            <p role="alert" className="mt-4 text-sm text-danger">
              {error}
            </p>
          )}
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-outline bg-surface px-4 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("cancel")}
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={busy}
              aria-busy={busy}
              onClick={onConfirm}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-on-accent transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
              {t("review_approve_recording")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

type PendingReviewSectionProps = {
  /** From the status snapshot; a change means the recordings on disk changed. */
  pendingReviewCount: number;
  /** Receives the status snapshot an approval or deletion returns. */
  onStatus: (status: StatusPayload) => void;
  onQueueEmpty?: () => void;
};

/**
 * Finished recordings held on this computer until the user approves their upload or deletes
 * them. Completion feedback remains briefly after the last review.
 */
const PendingReviewSection = ({
  pendingReviewCount,
  onStatus,
  onQueueEmpty,
}: PendingReviewSectionProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  const [recordings, setRecordings] = useState<ReviewRecording[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [approvingAllCount, setApprovingAllCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReviewRecording | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [loadingScreenshotsId, setLoadingScreenshotsId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    recording: ReviewRecording;
    screenshots: ScreenshotRef[];
  } | null>(null);
  const [feedback, setFeedback] = useState<"approved" | "deleted" | null>(null);
  // Screenshot filenames left out of each recording's upload, by recording id.
  const [excluded, setExcluded] = useState<Map<string, ReadonlySet<string>>>(new Map());
  const [approveTarget, setApproveTarget] = useState<ReviewRecording | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);
  const focusAfterReview = useRef(false);
  const completedInViewer = useRef(false);

  const excludedFrom = (recordingId: string) => excluded.get(recordingId) ?? new Set<string>();
  const toggleExcluded = ({ recordingId, filename }: ScreenshotRef) =>
    setExcluded(current => {
      const next = new Set(current.get(recordingId));
      if (!next.delete(filename)) next.add(filename);
      return new Map(current).set(recordingId, next);
    });

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 6000);
    return () => window.clearTimeout(timer);
  }, [feedback, recordings.length]);

  useEffect(() => {
    if (
      !focusAfterReview.current ||
      preview ||
      approveTarget ||
      deleteTarget ||
      busyId !== null ||
      approvingAllCount > 0
    )
      return;
    focusAfterReview.current = false;
    if (recordings.length) headingRef.current?.focus({ preventScroll: true });
    else onQueueEmpty?.();
  }, [
    preview,
    approveTarget,
    deleteTarget,
    recordings.length,
    feedback,
    busyId,
    approvingAllCount,
    onQueueEmpty,
  ]);

  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }
    let cancelled = false;
    get_review_recordings().then(
      payload => {
        if (cancelled) return;
        setRecordings(payload.recordings);
        setLoadError(null);
      },
      err => {
        if (!cancelled) setLoadError(formatApiRequestError(err, t));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [pendingReviewCount, t]);

  const visible =
    frontendCapabilities.localRecording &&
    (recordings.length > 0 || !!error || !!loadError || !!feedback || !!preview);
  const reviewTarget = splitHash(location.hash).section === PENDING_REVIEW_SECTION;
  useEffect(() => {
    if (!reviewTarget || !visible) {
      setHighlighted(false);
      return;
    }
    // Wait for the asynchronously loaded queue, and repeat when the menu is clicked on this page.
    headingRef.current?.focus({ preventScroll: true });
    sectionRef.current?.scrollIntoView({
      block: "start",
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
    setHighlighted(true);
    const timer = window.setTimeout(() => setHighlighted(false), 3000);
    return () => window.clearTimeout(timer);
  }, [location.key, reviewTarget, visible]);

  const removeFromList = (id: string) => {
    setRecordings(current => current.filter(recording => recording.id !== id));
    setExcluded(current => {
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  };

  const handleApprove = async (toApprove: ReviewRecording[]) => {
    // Snapshot the displayed queue: sessions arriving during approval require their own decision.
    setApprovingAllCount(toApprove.length > 1 ? toApprove.length : 0);
    let approved = 0;
    try {
      for (const recording of toApprove) {
        setBusyId(recording.id);
        onStatus(await approve_review_recording(recording.id, [...excludedFrom(recording.id)]));
        removeFromList(recording.id);
        approved += 1;
        setApprovedCount(approved);
        setError(null);
        setApproveError(null);
        setApproveTarget(null);
        completedInViewer.current = preview !== null;
        setPreview(null);
        setFeedback("approved");
      }
    } catch (err) {
      // Stop here so the failed and remaining sessions stay available for a deliberate retry.
      setError(formatApiRequestError(err, t));
      setApproveError(formatApiRequestError(err, t));
    } finally {
      focusAfterReview.current = approved > 0;
      setBusyId(null);
      setApprovingAllCount(0);
    }
  };

  const handleConfirmApprove = async () => {
    if (!approveTarget) return;
    await handleApprove([approveTarget]);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      onStatus(await delete_review_recording(deleteTarget.id));
      removeFromList(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteError(null);
      setError(null);
      completedInViewer.current = preview !== null;
      setPreview(null);
      setFeedback("deleted");
      focusAfterReview.current = true;
    } catch (err) {
      setDeleteError(formatApiRequestError(err, t));
    } finally {
      setBusyId(null);
    }
  };

  const handleViewScreenshots = async (recording: ReviewRecording) => {
    setLoadingScreenshotsId(recording.id);
    try {
      const { screenshots } = await get_review_screenshots(recording.id);
      setPreview(
        screenshots.length
          ? {
              recording,
              screenshots: screenshots.map(filename => ({ recordingId: recording.id, filename })),
            }
          : null,
      );
      setError(null);
    } catch (err) {
      setError(formatApiRequestError(err, t));
    } finally {
      setLoadingScreenshotsId(null);
    }
  };

  const openDelete = (recording: ReviewRecording) => {
    setDeleteError(null);
    setDeleteTarget(recording);
  };
  const deleteDialog = (
    <RecordingDeleteDialog
      open={deleteTarget !== null}
      busy={deleteTarget !== null && busyId === deleteTarget.id}
      error={deleteError}
      location="local"
      onCancel={() => setDeleteTarget(null)}
      onConfirm={handleConfirmDelete}
    />
  );

  if (!visible) {
    return null;
  }

  const queueCleared = recordings.length === 0 && !!feedback && !error && !loadError;
  return (
    <section
      ref={sectionRef}
      id={PENDING_REVIEW_SECTION}
      aria-labelledby="session-review-title"
      className={`scroll-mt-6 rounded-2xl border border-warning/25 p-4 motion-safe:transition-[box-shadow,background-color] motion-safe:duration-700 @[700px]:p-5 ${highlighted ? "bg-info/5 ring-2 ring-info" : "bg-warning/5"}`}
    >
      <div className="mb-4 flex flex-wrap items-start gap-3">
        {!queueCleared ? (
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-warning-strong" aria-hidden="true" />
        ) : (
          <Check className="mt-0.5 h-5 w-5 shrink-0 text-info" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              ref={headingRef}
              id="session-review-title"
              tabIndex={-1}
              className="rounded text-lg font-semibold text-text focus:outline-none"
            >
              {t(queueCleared ? "review_all_caught_up" : "review_recordings_title")}
            </h2>
            {recordings.length > 0 && (
              <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning-strong tabular-nums">
                {recordings.length}
              </span>
            )}
          </div>
          {recordings.length > 0 && (
            <p className="mt-1 text-sm text-text-mid">{t("review_recordings_subtitle")}</p>
          )}
        </div>
        {(recordings.length > 1 || approvingAllCount > 0) && (
          <button
            type="button"
            disabled={busyId !== null || loadingScreenshotsId !== null || approvingAllCount > 0}
            aria-busy={approvingAllCount > 0}
            onClick={() => handleApprove([...recordings])}
            className="inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg border border-info/30 bg-surface px-3 py-2 text-sm font-medium text-info transition hover:bg-info/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info disabled:cursor-not-allowed disabled:opacity-60"
          >
            {approvingAllCount > 0 ? (
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            ) : (
              <Upload size={16} aria-hidden="true" />
            )}
            {t("review_approve_all", { count: approvingAllCount || recordings.length })}
          </button>
        )}
      </div>
      {feedback && (
        <p role="status" className="mb-3 text-sm text-text-mid">
          {feedback === "approved"
            ? t("review_approved", { count: approvedCount })
            : t("review_deleted")}
        </p>
      )}
      {(error || loadError) && !preview && (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error || loadError}
        </p>
      )}
      <ul className="flex flex-col gap-3">
        {recordings.map(recording => (
          <li key={recording.id}>
            <ReviewRecordingCard
              recording={recording}
              excludedCount={excludedFrom(recording.id).size}
              busy={busyId !== null || loadingScreenshotsId !== null || approvingAllCount > 0}
              loadingScreenshots={loadingScreenshotsId === recording.id}
              approving={busyId === recording.id && deleteTarget === null && approveTarget === null}
              onApprove={() => {
                setApproveError(null);
                setApproveTarget(recording);
              }}
              onDelete={() => openDelete(recording)}
              onViewScreenshots={() => handleViewScreenshots(recording)}
            />
          </li>
        ))}
      </ul>
      <ScreenshotCarouselDialog
        open={preview !== null}
        screenshots={preview?.screenshots ?? []}
        onOpenChange={open => {
          if (!open) setPreview(null);
        }}
        onCloseAutoFocus={event => {
          // The reviewed card may have been removed; focus is moved to the queue or session list.
          if (!completedInViewer.current) return;
          event.preventDefault();
          completedInViewer.current = false;
          if (recordings.length) headingRef.current?.focus({ preventScroll: true });
          else onQueueEmpty?.();
        }}
        title={preview && <ReviewTimestamp startedAt={preview.recording.started_at} />}
        resolveUrl={reviewScreenshotUrl}
        isDimmed={screenshot => excludedFrom(screenshot.recordingId).has(screenshot.filename)}
        slideAction={screenshot => {
          const isExcluded = excludedFrom(screenshot.recordingId).has(screenshot.filename);
          return (
            <button
              type="button"
              onClick={() => toggleExcluded(screenshot)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium shadow-sm transition ${
                isExcluded
                  ? "border-warning/30 bg-warning/10 text-warning-strong hover:bg-warning/15"
                  : "border-outline bg-muted-surface text-text-dim hover:text-text"
              }`}
            >
              {isExcluded ? <ImagePlus size={14} /> : <ImageOff size={14} />}
              {t(isExcluded ? "review_include_screenshot" : "review_exclude_screenshot")}
            </button>
          );
        }}
        footer={
          preview && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm font-medium text-text">
                  <ReviewTimestamp startedAt={preview.recording.started_at} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ReviewActions
                    busy={busyId !== null}
                    approving={busyId === preview.recording.id && deleteTarget === null}
                    onApprove={() => {
                      setApproveError(null);
                      setPreview(null);
                      setApproveTarget(preview.recording);
                    }}
                    onDelete={() => openDelete(preview.recording)}
                  />
                </div>
              </div>
              {error && (
                <p role="alert" className="mt-2 text-sm text-danger">
                  {error}
                </p>
              )}
              {deleteDialog}
            </>
          )
        }
      />
      <ApproveReviewDialog
        recording={approveTarget ?? undefined}
        excludedCount={approveTarget ? excludedFrom(approveTarget.id).size : 0}
        busy={approveTarget !== null && busyId === approveTarget.id}
        error={approveError ?? undefined}
        onCancel={() => setApproveTarget(null)}
        onConfirm={() => void handleConfirmApprove()}
      />
      {!preview && deleteDialog}
    </section>
  );
};

export default PendingReviewSection;
